/**
 * DeleteEntityCommand - Generic command for deleting entities (StepNode, StrategyBlock)
 *
 * Implements the Command pattern for undoable entity deletion.
 * Delegates to FlowCoordinator for actual deletion logic.
 *
 * Generic Architecture:
 * - TEntity extends FlowEntity (StepNode, StrategyBlock)
 * - TService (StepNodeScreenService, StrategyBlockService)
 * - Uses ONLY generic coordinator methods (deleteEntities, restoreEntities)
 * - Supports batch deletion (multiple entities at once)
 *
 * Usage:
 * ```typescript
 * // Delete single V2 StepNode
 * const command = new DeleteEntityCommand<FlowStepNode, StepNodeScreenService>(
 *   ['screen-1'],
 *   screenContext
 * );
 *
 * // Delete multiple blocks
 * const command = new DeleteEntityCommand<StrategyBlock, StrategyBlockService>(
 *   ['block-1', 'block-2', 'block-3'],
 *   blockContext
 * );
 *
 * await commandManager.execute(command);
 * ```
 *
 * Related Requirements:
 * - FR-P1-003: Commands use coordinator ONLY for all operations
 * - FR-P3-001: Generic FlowCoordinator interface
 * - FR-P3-004: Generic CommandContext
 */

import { Command, CommandResult } from './Command';
import { CommandContext } from './CommandContext';
import type { FlowEntity } from '@modules/reactFlow/types/flow-canvas-types';

const uniqueIds = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));

/**
 * DeleteEntityCommand - Generic command for deleting flow entities
 *
 * @template TEntity - Entity type extending FlowEntity (StepNode, StrategyBlock)
 * @template TService - Firebase service type (StepNodeScreenService, StrategyBlockService)
 */
export class DeleteEntityCommand<
  TEntity extends FlowEntity,
  TService
> implements Command {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  // Backup data for undo
  private deletedEntities: TEntity[] = [];
  private affectedEntitiesBackup?: Map<string, TEntity>;
  private deletedTransitionIds: string[] = [];
  private readonly entityIds: string[];

  /**
   * @param entityIds - Array of entity IDs to delete
   * @param context - Command execution context
   */
  constructor(
    entityIds: string[],
    private context: CommandContext<TEntity, TService>
  ) {
    this.entityIds = uniqueIds(entityIds);
    this.id = `delete-entity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.description = `Delete ${this.entityIds.length} entity(ies)`;
    this.timestamp = Date.now();
  }

  /**
   * Execute command - Delete entities via coordinator
   *
   * Flow:
   * 1. Backup entities for undo
   * 2. Call coordinator.deleteEntities() - handles Registry + Canvas + Firebase + transitions
   * 3. Store deleted transition IDs for reporting
   * 4. Return success result
   *
   * @returns Promise resolving with command result
   */
  async execute(): Promise<CommandResult> {
    try {
      console.log(`[DeleteEntityCommand] Executing delete for: ${this.entityIds.join(', ')}`);

      // Step 1: Backup entities for undo
      // NOTE: Use getEntity() which supports both entityId and Firestore id
      this.deletedEntities = this.entityIds
        .map(id => this.context.coordinator.getEntity(id))
        .filter((e): e is TEntity => e !== undefined);

      if (this.deletedEntities.length === 0) {
        throw new Error('No entities found to delete');
      }

      // Step 2: Delete via coordinator (handles everything atomically)
      const result = await this.context.coordinator.deleteEntities(this.entityIds);

      this.deletedTransitionIds = result.deletedTransitionIds;
      this.affectedEntitiesBackup = result.affectedScreensBackup;

      console.log(`[DeleteEntityCommand] ✅ Deleted ${this.entityIds.length} entities, ${this.deletedTransitionIds.length} transitions`);

      return {
        success: true,
        affectedEntities: {
          screens: this.entityIds,
          transitions: this.deletedTransitionIds
        }
      };
    } catch (error) {
      console.error('[DeleteEntityCommand] ❌ Delete failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Undo command - Restore deleted entities via coordinator
   *
   * Flow:
   * 1. Call coordinator.restoreEntities() - handles Registry + Canvas + Firebase
   * 2. Return success result
   *
   * @returns Promise resolving with command result
   */
  async undo(): Promise<CommandResult> {
    if (this.deletedEntities.length === 0) {
      return {
        success: false,
        error: new Error('No entities to restore'),
        affectedEntities: {}
      };
    }

    try {
      console.log(`[DeleteEntityCommand] Undoing delete for: ${this.entityIds.join(', ')}`);

      // Coordinator handles EVERYTHING (Registry + Canvas + Firebase)
      await this.context.coordinator.restoreEntities(this.deletedEntities, this.affectedEntitiesBackup);

      console.log('[DeleteEntityCommand] ✅ Undo successful');

      return {
        success: true,
        affectedEntities: {
          screens: this.entityIds,
          transitions: this.deletedTransitionIds
        }
      };
    } catch (error) {
      console.error('[DeleteEntityCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Undo failed'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Check if command can be executed
   * @returns true if at least one entity exists
   *
   * NOTE: Uses getEntity() which supports both entityId and Firestore id
   * (Canvas passes entityId, but entity.id is Firestore id)
   */
  canExecute(): boolean {
    console.log('[DeleteEntityCommand] canExecute() check:', {
      entityIds: this.entityIds,
      coordinatorType: this.context.coordinator.constructor.name,
      hasGetEntity: typeof this.context.coordinator.getEntity === 'function'
    });

    const result = this.entityIds.some(id => {
      const entity = this.context.coordinator.getEntity(id);
      console.log(`[DeleteEntityCommand]   - ID "${id}": ${entity ? 'FOUND' : 'NOT FOUND'}`,
        entity ? { entityId: (entity as any).entityId, firestoreId: entity.id } : {}
      );
      return entity !== undefined;
    });

    console.log('[DeleteEntityCommand] canExecute() result:', result);
    return result;
  }

  /**
   * Check if command can be undone
   * @returns true if entities were deleted successfully
   */
  canUndo(): boolean {
    return this.deletedEntities.length > 0;
  }
}
